import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Mic, MicOff, Square, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../shared/components/Button'
import { useVoiceRecorder } from '../../shared/hooks/useVoiceRecorder'
import { capture } from '../../shared/lib/offlineCaptures'

export type LiveSheetKind = 'note' | 'add_place' | 'voice'

interface LiveCaptureSheetProps {
  kind: LiveSheetKind | null
  tripId: string
  focusPointId?: string | null
  focusPointTitle?: string | null
  busy?: boolean
  onClose: () => void
  onSaveNote: (text: string) => void
  onAddPlace: (title: string, address: string) => void
}

export function LiveCaptureSheet({
  kind,
  tripId,
  focusPointId,
  focusPointTitle,
  busy,
  onClose,
  onSaveNote,
  onAddPlace,
}: LiveCaptureSheetProps) {
  const { t } = useTranslation()
  const [note, setNote] = useState('')
  const [placeTitle, setPlaceTitle] = useState('')
  const [placeAddress, setPlaceAddress] = useState('')

  // Voice state
  const recorder = useVoiceRecorder()
  const [transcript, setTranscript] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (!kind) return
    setNote('')
    setPlaceTitle('')
    setPlaceAddress('')
    setTranscript('')
    setUploadError(null)
    if (kind !== 'voice') recorder.reset()
  }, [kind]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStopAndTranscribe() {
    recorder.stop()
    // Wait a tick for onstop to fire and set audioBlob
  }

  useEffect(() => {
    if (recorder.state !== 'stopped' || !recorder.audioBlob) return
    setUploading(true)
    setUploadError(null)
    capture({tripId,kind:'voice',pointId:focusPointId ?? null,blob:recorder.audioBlob,fileName:'voice-note.webm'})
      .then((r) => {
        setTranscript(r.transcript ?? '')
        if (r.queued) setUploadError('Voice saved on this device; transcription will run after reconnecting.')
      })
      .catch((err) => setUploadError(err instanceof Error ? err.message : 'Could not save voice recording.'))
      .finally(() => setUploading(false))
  }, [recorder.state, recorder.audioBlob]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaveVoiceNote(e: FormEvent) {
    e.preventDefault()
    if (!transcript.trim()) return
    onSaveNote(transcript.trim())
  }

  if (!kind) return null

  const secondsRecorded = Math.round(recorder.durationMs / 1000)

  function handleNoteSubmit(e: FormEvent) {
    e.preventDefault()
    if (!note.trim()) return
    onSaveNote(note.trim())
  }

  function handlePlaceSubmit(e: FormEvent) {
    e.preventDefault()
    if (!placeTitle.trim()) return
    onAddPlace(placeTitle.trim(), placeAddress.trim())
  }

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 p-4" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="live-sheet-title"
        className="w-full max-w-lg rounded-t-st-lg border border-st-line bg-st-surface p-5 shadow-lg"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="live-sheet-title" className="font-display text-lg font-semibold text-st-text">
            {kind === 'note' && t('live.quick_note')}
            {kind === 'add_place' && t('live.add_place')}
            {kind === 'voice' && t('live.voice_note')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-surface-2"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {kind === 'note' && (
          <form onSubmit={handleNoteSubmit} className="space-y-3">
            {focusPointTitle && (
              <p className="text-sm text-st-muted">
                For <span className="font-medium text-st-text">{focusPointTitle}</span>
              </p>
            )}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              autoFocus
              placeholder={t('live.what_happening')}
              className="w-full rounded-st-md border border-st-line bg-st-surface p-3 text-sm text-st-text focus:border-st-primary focus:outline-none"
            />
            <Button type="submit" variant="accent" disabled={!note.trim() || busy}>
              {busy ? t('live.saving') : t('live.save_note')}
            </Button>
          </form>
        )}

        {kind === 'add_place' && (
          <form onSubmit={handlePlaceSubmit} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-st-text">{t('live.place_name')}</span>
              <input
                value={placeTitle}
                onChange={(e) => setPlaceTitle(e.target.value)}
                autoFocus
                placeholder={t('live.place_name_hint')}
                className="input w-full"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-st-text">{t('live.address_optional')}</span>
              <input
                value={placeAddress}
                onChange={(e) => setPlaceAddress(e.target.value)}
                placeholder="Street or area"
                className="input w-full"
              />
            </label>
            <Button type="submit" variant="accent" disabled={!placeTitle.trim() || busy}>
              {busy ? t('live.adding') : t('live.add_to_trip')}
            </Button>
          </form>
        )}

        {kind === 'voice' && (
          <div className="space-y-4">
            {recorder.error && (
              <p className="text-sm text-red-600">{recorder.error}</p>
            )}

            {/* Recording controls */}
            {recorder.state === 'idle' && (
              <div className="flex flex-col items-center gap-3 py-2">
                <button
                  type="button"
                  onClick={recorder.start}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600 active:scale-95"
                  aria-label={t('live.tap_to_record')}
                >
                  <Mic className="h-7 w-7" />
                </button>
                <p className="text-sm text-st-muted">{t('live.tap_to_record')}</p>
              </div>
            )}

            {recorder.state === 'recording' && (
              <div className="flex flex-col items-center gap-3 py-2">
                <button
                  type="button"
                  onClick={handleStopAndTranscribe}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600 active:scale-95"
                  aria-label={t('live.recording')}
                >
                  <Square className="h-7 w-7 fill-white" />
                </button>
                <p className="animate-pulse text-sm text-red-500">{t('live.recording')}</p>
              </div>
            )}

            {(recorder.state === 'stopped' || uploading || transcript || uploadError) && (
              <div className="space-y-3">
                {recorder.audioUrl && (
                  <div className="flex items-center gap-3">
                    <audio ref={audioRef} src={recorder.audioUrl} controls className="w-full" />
                    <span className="shrink-0 text-xs text-st-muted">{secondsRecorded}s</span>
                  </div>
                )}

                {uploading && (
                  <p className="text-sm text-st-muted">{t('live.transcribing')}</p>
                )}

                {uploadError && (
                  <p className="text-sm text-red-600">{uploadError}</p>
                )}

                <form onSubmit={handleSaveVoiceNote} className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-st-text">
                      {uploading ? t('live.transcript_loading') : t('live.transcript')}
                    </span>
                    <textarea
                      value={transcript}
                      onChange={(e) => setTranscript(e.target.value)}
                      rows={3}
                      placeholder={t('live.transcript_placeholder')}
                      className="w-full rounded-st-md border border-st-line bg-st-surface p-3 text-sm text-st-text focus:border-st-primary focus:outline-none"
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button type="submit" variant="accent" disabled={!transcript.trim() || uploading || busy}>
                      {busy ? t('live.saving') : t('live.save_as_note')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { recorder.reset(); setTranscript(''); setUploadError(null) }}>
                      <MicOff className="mr-1 h-4 w-4" />
                      {t('live.re_record')}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        .input {
          min-height: 2.75rem;
          border-radius: var(--radius-st-md);
          border: 1px solid var(--color-st-line);
          background: var(--color-st-surface);
          padding: 0 0.875rem;
          color: var(--color-st-text);
          font-size: 0.95rem;
        }
        .input:focus { outline: 2px solid var(--color-st-info); outline-offset: 1px; }
      `}</style>
    </div>
  )
}
